import { Injectable } from '@angular/core';
import { FmTask } from '@core/api';

export interface TicketEquipmentRow {
  archObjId: string;
  equipment: string;
  incidentEquipmentId: string;
  equipmentId: string;
  room: string;
  quantity: number;
  type: string;
  parentEquipment: string;
  parentEquipmentQuantity: number;
  assetCharacteristic: string;
  parentSystemStatus: string;
  parentUserStatus: string;
  systemStatus: string;
  userStatus: string;
  economicUnit: string;
  technicalPlace: string;
  technicalPlaceDescription: string;
  postalCode: string;
  city: string;
  district: string;
  street: string;
  operatingArea: string;
  equipmentClass: string;
  createDate?: Date;
  deleteDate?: Date;
  createdOn: string;
  yearOfConstruction: string;
  companyCode: string;
  manufacturer: string;
  manufacturerSerialNumber: string;
  inventoryNumber: string;
  documents?: any[];
}

@Injectable()
export class TaskEquipmentService {
  buildEquipmentByArchObjId(
    task?: FmTask
  ): Record<string, TicketEquipmentRow[]> {
    const rows = this.mapEquipmentRows(task);
    const grouped: Record<string, TicketEquipmentRow[]> = {};

    for (const row of rows) {
      if (!grouped[row.archObjId]) {
        grouped[row.archObjId] = [];
      }
      grouped[row.archObjId].push(row);
    }
    return grouped;
  }

  buildEquipmentColumns(options?: { includeDocumentation?: boolean }): any[] {
    const columns: any[] = [
      {
        header: 'Equipment-ID',
        field: 'equipmentId',
        sortable: true,
        formatter: (row: TicketEquipmentRow) => row.equipmentId || '-',
      },
      {
        header: 'Equipment',
        field: 'equipment',
        sortable: true,
        formatter: (row: TicketEquipmentRow) => row.equipment || '-',
      },
      {
        header: 'Raum',
        field: 'room',
        sortable: true,
        formatter: (row: TicketEquipmentRow) => row.room || '-',
      },
      {
        header: 'Menge',
        field: 'quantity',
        sortable: true,
        formatter: (row: TicketEquipmentRow) => row.quantity ?? 0,
      },
      {
        header: 'Typ',
        field: 'type',
        sortable: true,
        formatter: (row: TicketEquipmentRow) => row.type || '-',
      },
    ];

    if (options?.includeDocumentation !== false) {
      columns.push({
        header: 'Dokumentation',
        field: 'documentation',
        sortable: false,
        width: '260px',
        formatter: () => '\u00A0',
      });
    }

    return columns;
  }

  private mapEquipmentRows(task?: FmTask): TicketEquipmentRow[] {
    const incidentEquipments = this.getIncidentEquipments(task);
    const documentsByIncidentEquipmentId =
      this.getDocumentsByIncidentEquipmentId(task);

    return incidentEquipments.map((incidentEquipment: any) => {
      const archObjId =
        String(incidentEquipment?.archObjId || '').trim() || 'unknown';
      const incidentEquipmentId = String(incidentEquipment?.id || '').trim();

      return {
        archObjId,
        equipment: String(incidentEquipment?.name || '').trim(),
        incidentEquipmentId,
        equipmentId: String(incidentEquipment?.equipmentId || '').trim(),
        room: String(incidentEquipment?.room || '').trim(),
        quantity: this.parseQuantity(incidentEquipment?.quantity),
        type: String(incidentEquipment?.type || '').trim(),
        parentEquipment: this.toText(incidentEquipment?.parentEquipmentId),
        parentEquipmentQuantity: this.parseQuantity(
          incidentEquipment?.parentEquipmentQuantity
        ),
        assetCharacteristic: this.toText(
          incidentEquipment?.assetCharacteristic
        ),
        parentSystemStatus: this.toText(
          incidentEquipment?.parentEquipmentSystemStatus
        ),
        parentUserStatus: this.toText(
          incidentEquipment?.parentEquipmentUserStatus
        ),
        parentArchObjId: String(
          incidentEquipment?.parentArchObjId || ''
        ).trim(),
        systemStatus: this.toText(incidentEquipment?.systemStatus),
        userStatus: this.toText(incidentEquipment?.userStatus),
        economicUnit: this.toText(incidentEquipment?.archObjId.slice(0, 5)),
        technicalPlace: this.toText(incidentEquipment?.archObjId),
        technicalPlaceDescription: this.toText(
          incidentEquipment?.archObjId.slice(-4)
        ),
        postalCode: this.toText(incidentEquipment?.postalCode),
        city: this.toText(incidentEquipment?.city),
        district: this.toText(incidentEquipment?.district),
        street: this.toText(incidentEquipment?.street),
        operatingArea: this.toText(incidentEquipment?.operatingArea),
        equipmentClass: this.toText(incidentEquipment?.equipmentClass),
        createDate: incidentEquipment?.linkCreateDate ? new Date(incidentEquipment.linkCreateDate) : undefined,
        deleteDate: incidentEquipment?.linkDeleteDate ? new Date(incidentEquipment.linkDeleteDate) : undefined,
        createdOn: this.toText(incidentEquipment?.changeDate),
        yearOfConstruction: this.toText(incidentEquipment?.yearOfConstruction),
        companyCode: this.toText(incidentEquipment?.companyCode),
        manufacturer: this.toText(incidentEquipment?.manufacturer),
        manufacturerSerialNumber: this.toText(
          incidentEquipment?.manufacturerSerialNumber
        ),
        inventoryNumber: this.toText(incidentEquipment?.inventoryNumber),
        documents: documentsByIncidentEquipmentId[incidentEquipmentId] || [],
      };
    });
  }

  private getDocumentsByIncidentEquipmentId(
    task?: FmTask
  ): Record<string, any[]> {
    const documents = ((task as any)?.fmDocument || []) as any[];
    const grouped: Record<string, any[]> = {};

    for (const document of documents) {
      const incidentEquipmentId = String(
        document?.incidentEquipmentId || ''
      ).trim();

      if (!incidentEquipmentId || document?.deleted) {
        continue;
      }

      if (!grouped[incidentEquipmentId]) {
        grouped[incidentEquipmentId] = [];
      }

      grouped[incidentEquipmentId].push(document);
    }

    return grouped;
  }

  private toText(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private parseQuantity(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  private getIncidentEquipments(task?: FmTask): any[] {
    const raw = (task as any)?.incidentEquipments;
    if (Array.isArray(raw)) {
      return raw;
    }

    if (!raw) {
      return [];
    }

    return [raw];
  }
}
